import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { AlertController, NavController, Platform } from '@ionic/angular';
import { Browser } from '@capacitor/browser';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import { IDEAMessageService, IDEATranslationsService } from '@idea-ionic/common';

import { AuthService } from './auth/auth.service';

import { environment as env } from '@env';
import { User } from '@models/user.model';

const ICONS_FROM_IONIC = ['flash-outline'];

/**
 * Main utilities for the app.
 */
@Injectable({ providedIn: 'root' })
export class AppService {
  initReady = false;
  authReady = false;
  fromDeepLink = false;

  user: User;

  customIconsPath: string;

  constructor(
    private platform: Platform,
    private navCtrl: NavController,
    private alertCtrl: AlertController,
    private auth: AuthService,
    private message: IDEAMessageService,
    private t: IDEATranslationsService
  ) {}

  /**
   * Whether we are running the app in developer mode (from localhost).
   */
  isDeveloperMode(): boolean {
    return env.debug;
  }

  /**
   * Whether we should display a UX designed for smaller screens.
   */
  isInMobileMode(): boolean {
    return this.platform.width() < 768;
  }

  /**
   * Navigate to a page by its path.
   */
  goTo(path: string[], options: { back?: boolean; root?: boolean; queryParams?: Params } = {}): void {
    const fullPath = path[0] === 'auth' ? path : ['user', ...path];

    if (options.back) this.navCtrl.navigateBack(fullPath, options);
    else if (options.root) this.navCtrl.navigateRoot(fullPath, options);
    else this.navCtrl.navigateForward(fullPath, options);
  }
  /**
   * Close the current page and navigate back, optionally displaying an error message.
   */
  closePage(errorMessage?: string, pathBack?: string[]): void {
    if (errorMessage) this.message.error(errorMessage);
    try {
      this.navCtrl.back();
    } catch (_) {
      this.goTo(pathBack || [], { back: true });
    }
  }

  /**
   * Utility to generate a numeric array.
   * Useful for skeleton interfaces.
   */
  generateNumericArray(length: number): number[] {
    return [...Array(length).keys()];
  }

  /**
   * Prefer a branded (custom) icon, if available.
   */
  icon(icon: string, altVersion = false): string {
    if (this.customIconsPath && !ICONS_FROM_IONIC.includes(icon))
      return this.customIconsPath.concat('/', icon, altVersion ? '-alt.svg' : '.svg');
    return icon;
  }

  /**
   * Logout the current user.
   */
  async logout(noAlert = false): Promise<void> {
    const doLogout = () => {
      this.auth.logout();
      window.location.assign('');
    };
    if (noAlert) return doLogout();

    const header = this.t._('COMMON.LOGOUT');
    const message = this.t._('COMMON.ARE_YOU_SURE');

    const buttons = [{ text: this.t._('COMMON.CANCEL') }, { text: this.t._('COMMON.LOGOUT'), handler: doLogout }];

    const alert = await this.alertCtrl.create({ header, message, buttons });
    alert.present();
  }

  /**
   * Get the main icon of the app.
   */
  getIcon(): string {
    return `assets/icons/icon.svg`;
  }
  /**
   * Get the menu icon of the app.
   */
  getMenuIcon(): string {
    return 'assets/icons/icon-menu.svg';
  }

  /**
   * Extract a datetime without timezones.
   */
  extractDateTime(date: string | Date): string {
    if (!(date instanceof Date)) date = new Date(date);
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  }
  /**
   * Extract a date without timezones.
   */
  extractDate(date: string | Date): string {
    if (!(date instanceof Date)) date = new Date(date);
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10).concat('T00:00');
  }

  /**
   * Open a base64-encoded file.
   */
  async downloadBase64File(
    base64: string,
    options: { type?: string; name?: string } = { type: 'application/pdf', name: 'file.pdf' }
  ): Promise<void> {
    const name = options.name ?? 'file.pdf';
    const type = options.type ?? 'application/pdf';

    if (this.platform.is('capacitor'))
      try {
        const path = name;
        const { uri } = await Filesystem.writeFile({ path, data: base64, directory: Directory.Cache });
        await FileOpener.open({ filePath: uri, contentType: type });
      } catch (error) {
        this.message.error('COMMON.OPERATION_FAILED');
      }
    else {
      const blob = this.createBlobFromBase64(base64, type);
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
    }
  }
  private createBlobFromBase64(base64: string, type: string): Blob {
    const binary = Buffer.from(base64, 'base64').toString('latin1');
    const len = binary.length;
    const buffer = new ArrayBuffer(len);
    const view = new Uint8Array(buffer);

    for (let i = 0; i < len; i += 1) {
      view[i] = binary.charCodeAt(i);
    }

    return new Blob([view], { type });
  }

  /**
   * Open a page in the official website.
   */
  async openPageOnWebsite(relativeUrl?: string): Promise<void> {
    const url = [env.idea.app.websiteURL, relativeUrl].join('/');
    await this.openURL(url);
  }

  /**
   * Make the app sleep (wait) for a number of seconds.
   */
  async sleepForNumSeconds(numSeconds: number): Promise<void> {
    return new Promise(resolve => setTimeout(() => resolve(), numSeconds * 1000));
  }

  /**
   * Open the URL in the browser.
   */
  async openURL(url: string): Promise<void> {
    const windowName = this.platform.is('ios') ? '_parent' : '_blank';
    await Browser.open({ url, windowName });
  }
}
